import React from 'react';
import { ChevronDown, ChevronRight, Folder } from 'react-feather';

import type { ContentHubBrandNode, ContentHubTeamNode } from '@/hooks/client/useContentHubPaths';

import styles from './ContentHubTree.module.css';

type Props = {
  teams: ContentHubTeamNode[];
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
};

function TreeCampaignNode({
  campaign,
  selectedFolderId,
  onSelect
}: {
  campaign: ContentHubBrandNode['campaigns'][number];
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
}) {
  const isSelected = selectedFolderId === campaign.id;
  const hasChildren = (campaign.collaborations?.length ?? 0) > 0;
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={styles.node}>
      <div
        className={`${styles.row} ${styles.campaignRow} ${isSelected ? styles.selected : ''}`}
        onClick={() => onSelect(campaign.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect(campaign.id);
        }}
      >
        <button
          type="button"
          className={styles.toggle}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
          disabled={!hasChildren}
        >
          {hasChildren ? isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} /> : null}
        </button>
        <div className={styles.content}>
          <span className={styles.icon} aria-hidden>
            <Folder size={16} />
          </span>
          <span className={styles.label} title={campaign.name}>
            {campaign.name}
          </span>
        </div>
      </div>

      {hasChildren && isOpen && (
        <div className={styles.children}>
          {(campaign.collaborations ?? []).map((collab) => {
            const label = collab.influencerHandle
              ? `${collab.influencerName} (${collab.influencerHandle})`
              : collab.influencerName;
            const isSelectedCollab = selectedFolderId === collab.id;
            return (
              <div
                key={collab.id}
                className={`${styles.row} ${styles.collabRow} ${
                  isSelectedCollab ? styles.selected : ''
                }`}
                onClick={() => onSelect(collab.id)}
              >
                <span className={styles.toggle} aria-hidden />
                <div className={styles.content}>
                  <span className={styles.icon} aria-hidden>
                    <Folder size={16} />
                  </span>
                  <span className={styles.label} title={label}>
                    {label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TreeBrandNode({
  brand,
  selectedFolderId,
  onSelect
}: {
  brand: ContentHubTeamNode['brands'][number];
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
}) {
  const isSelected = selectedFolderId === brand.id;
  const hasChildren = (brand.campaigns?.length ?? 0) > 0;
  const [isOpen, setIsOpen] = React.useState(false);
  const [logoError, setLogoError] = React.useState(false);

  return (
    <div className={styles.node}>
      <div
        className={`${styles.row} ${styles.brandRow} ${isSelected ? styles.selected : ''}`}
        onClick={() => onSelect(brand.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect(brand.id);
        }}
      >
        <button
          type="button"
          className={styles.toggle}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
          disabled={!hasChildren}
        >
          {hasChildren ? isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} /> : null}
        </button>
        <div className={styles.content}>
          {brand.logoUrl && !logoError ? (
            <img
              className={styles.brandLogo}
              src={brand.logoUrl}
              alt={brand.name}
              loading="lazy"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className={styles.icon} aria-hidden>
              <Folder size={16} />
            </span>
          )}
          <span className={styles.label} title={brand.name}>
            {brand.name}
          </span>
        </div>
      </div>

      {hasChildren && isOpen && (
        <div className={styles.children}>
          {(brand.campaigns ?? []).map((campaign) => (
            <TreeCampaignNode
              key={campaign.id}
              campaign={campaign}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeTeamNode({
  team,
  selectedFolderId,
  onSelect
}: {
  team: ContentHubTeamNode;
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
}) {
  const isSelected = selectedFolderId === team.id;
  const hasChildren = (team.brands?.length ?? 0) > 0;
  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <div className={styles.node}>
      <div
        className={`${styles.row} ${isSelected ? styles.selected : ''}`}
        onClick={() => onSelect(team.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect(team.id);
        }}
      >
        <button
          type="button"
          className={styles.toggle}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
          disabled={!hasChildren}
        >
          {hasChildren ? isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} /> : null}
        </button>
        <div className={styles.content}>
          <span className={styles.icon} aria-hidden>
            <Folder size={16} />
          </span>
          <span className={styles.label} title={team.name}>
            {team.name}
          </span>
        </div>
      </div>

      {hasChildren && isOpen && (
        <div className={styles.children}>
          {(team.brands ?? []).map((brand) => (
            <TreeBrandNode
              key={brand.id}
              brand={brand}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ContentHubTree({ teams, selectedFolderId, onSelect }: Props) {
  return (
    <div className={styles.tree}>
      {teams.map((team) => (
        <TreeTeamNode
          key={team.id}
          team={team}
          selectedFolderId={selectedFolderId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
